import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { useDocumentViewport } from "@/features/document-viewport/context";
import type { AiCountPage } from "./ai-count-types";

export function AiCountMarks({
  marks,
  tags,
  selectedMeasurementIds,
  selectedMarkId,
  interactive = true,
  onSelect,
}: {
  marks: AiCountPage["proposals"];
  tags: Array<{ tagId: Id<"takeoffPilotExtractedTags">; rawValue: string; colorHex?: string }>;
  selectedMeasurementIds: Id<"takeoffPilotMeasurements">[];
  selectedMarkId: string | null;
  interactive?: boolean;
  onSelect: (id: string) => void;
}) {
  const viewport = useDocumentViewport();
  const selectedIds = new Set(selectedMeasurementIds);
  return (
    <div className="pointer-events-none absolute inset-0" inert={!interactive}>
      {marks
        .filter(
          (mark) =>
            tags.some((tag) => tag.tagId === mark.tagId) &&
            (!mark.geometryJson ||
              JSON.parse(mark.geometryJson).kind === "point"),
        )
        .map((mark) => {
          const screen = viewport.transforms.pageToScreenPoint(mark);
          const tag = tags.find((tag) => tag.tagId === mark.tagId)!;
          const selected = mark.measurementId ? selectedIds.has(mark.measurementId) : mark._id === selectedMarkId;
          const color =
            mark.reviewStatus === "rejected" || mark.scope === "excluded"
              ? "#64748b"
              : mark.scope === "uncertain" || !mark.identityResolved
                ? "#b45309"
                : (tag.colorHex ?? "#15803d");
          const description = `${tag.rawValue} fixture, ${mark.reviewStatus.replaceAll("_", " ")}${mark.scope !== "included" ? `, ${mark.scope} scope` : ""}`;
          return (
            <button
              key={mark._id}
              type="button"
              data-viewport-tools-ui
              className="pointer-events-auto absolute flex size-10 items-center justify-center rounded-full focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-800"
              style={{ left: screen.x, top: screen.y, transform: "translate(-50%, -50%)" }}
              aria-label={description}
              aria-pressed={selected}
              title={description}
              onClick={() => onSelect(mark._id)}
            >
              {selected && <span aria-hidden className="pointer-events-none absolute inset-0.5 rounded-full border-[3px] border-sky-600 bg-sky-500/10 shadow-[0_0_0_2px_white,0_0_0_3px_#075985]" />}
              <span
                aria-hidden
                className={`relative flex size-5 items-center justify-center rounded-full border-2 ${selected ? "" : "shadow-[0_0_0_2px_white,0_0_0_3px_rgb(15_23_42_/0.6)]"}`}
                style={{
                  borderColor: color,
                  backgroundColor:
                    mark.reviewStatus === "accepted"
                      ? `${color}40`
                      : "transparent",
                  borderStyle:
                    mark.reviewStatus === "pending_review" ? "dashed" : "solid",
                }}
              >
                {mark.reviewStatus === "rejected" ? "×" : selected && <span className="size-1 rounded-full bg-sky-700" />}
              </span>
            </button>
          );
        })}
    </div>
  );
}
